// Server idle-window tracking for the "about to be signed out" warning.
//
// The server signs a session out when a token refresh arrives more than
// idleTimeout seconds after the session was last used, and "used" means a
// login or a token refresh (auth.Service.RefreshTokens, idle_policy.go). The
// login and refresh responses carry that window as `idleTimeout` (seconds).
// This module records when the window last restarted and how long it is, so
// IdleWarning can warn before it runs out.
//
// A tenant switch re-mints the access token without touching the session, so
// it changes the window's LENGTH (the new persona's) but not its start.
//
// The window belongs to the session, which every tab shares through the
// refresh cookie, so the record is kept in localStorage and a refresh in one
// tab moves the deadline in all of them (the `storage` event notifies the
// others). Storage access is wrapped: a private window or blocked storage
// falls back to this tab's in-memory copy.
//
// Nothing here talks to the server, and reading the record never counts as
// activity.

export const SESSION_ACTIVITY_KEY = 'vharta-session-activity'
const SIGNED_OUT_NOTICE_KEY = 'vharta-signed-out-notice'

export interface SessionActivity {
  /** Epoch ms of the last login or refresh: when the server's idle window restarted. */
  lastActivityAt: number
  /** Length of the idle window, ms. */
  idleTimeoutMs: number
}

let memory: SessionActivity | null = null
const listeners = new Set<() => void>()

function parse(raw: string | null): SessionActivity | null {
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<SessionActivity>
    if (
      typeof v.lastActivityAt === 'number' &&
      Number.isFinite(v.lastActivityAt) &&
      typeof v.idleTimeoutMs === 'number' &&
      Number.isFinite(v.idleTimeoutMs) &&
      v.idleTimeoutMs > 0
    ) {
      return { lastActivityAt: v.lastActivityAt, idleTimeoutMs: v.idleTimeoutMs }
    }
  } catch {
    // fall through
  }
  return null
}

function write(value: SessionActivity | null): void {
  memory = value
  try {
    if (value) localStorage.setItem(SESSION_ACTIVITY_KEY, JSON.stringify(value))
    else localStorage.removeItem(SESSION_ACTIVITY_KEY)
  } catch {
    // Storage unavailable: the in-memory copy still serves this tab.
  }
  listeners.forEach((fn) => fn())
}

/** The current record, or null when the idle window is unknown (dev tokens, signed out). */
export function getSessionActivity(): SessionActivity | null {
  try {
    const raw = localStorage.getItem(SESSION_ACTIVITY_KEY)
    // Another tab may have cleared or updated it: storage wins when readable.
    return parse(raw)
  } catch {
    return memory
  }
}

/**
 * Records a successful login or token refresh: the server's idle window
 * restarted now. `idleTimeoutSeconds` is the response's `idleTimeout`; when it
 * is missing (dev-mode tokens) the record is cleared and no warning is shown.
 */
export function recordSessionActivity(idleTimeoutSeconds: number | undefined, now: number = Date.now()): void {
  if (typeof idleTimeoutSeconds !== 'number' || !Number.isFinite(idleTimeoutSeconds) || idleTimeoutSeconds <= 0) {
    write(null)
    return
  }
  write({ lastActivityAt: now, idleTimeoutMs: idleTimeoutSeconds * 1000 })
}

/**
 * Records a tenant switch: the new persona's window length applies from the
 * session's existing start (a switch does not restart the server's window).
 * With no existing record the window is taken to start now.
 */
export function recordTenantSwitch(idleTimeoutSeconds: number | undefined, now: number = Date.now()): void {
  if (typeof idleTimeoutSeconds !== 'number' || !Number.isFinite(idleTimeoutSeconds) || idleTimeoutSeconds <= 0) {
    write(null)
    return
  }
  const existing = getSessionActivity()
  write({ lastActivityAt: existing?.lastActivityAt ?? now, idleTimeoutMs: idleTimeoutSeconds * 1000 })
}

/** Forgets the record (sign-out). */
export function clearSessionActivity(): void {
  write(null)
}

function onStorage(e: StorageEvent): void {
  // e.key is null when another tab cleared all of localStorage.
  if (e.key !== null && e.key !== SESSION_ACTIVITY_KEY) return
  memory = parse(e.newValue)
  listeners.forEach((fn) => fn())
}

/** Calls `fn` whenever the record changes, in this tab or another one. Returns the unsubscribe. */
export function subscribeSessionActivity(fn: () => void): () => void {
  if (listeners.size === 0 && typeof window !== 'undefined') window.addEventListener('storage', onStorage)
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
    if (listeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
  }
}

/**
 * A one-time message for the sign-in page, e.g. the server's "You were signed
 * out due to inactivity" after the idle warning's refresh was refused. Kept in
 * sessionStorage so it survives the redirect but not the tab.
 */
export function setSignedOutNotice(message: string): void {
  try {
    sessionStorage.setItem(SIGNED_OUT_NOTICE_KEY, message)
  } catch {
    // Not shown; the sign-out itself is unaffected.
  }
}

export function peekSignedOutNotice(): string | null {
  try {
    return sessionStorage.getItem(SIGNED_OUT_NOTICE_KEY)
  } catch {
    return null
  }
}

export function clearSignedOutNotice(): void {
  try {
    sessionStorage.removeItem(SIGNED_OUT_NOTICE_KEY)
  } catch {
    // ignore
  }
}
